import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ZoomableImage from '@components/1-atoms/ZoomableImage';
import useAuthImage from '@hooks/useAuthImage';
import { API_BASE_URL } from '@api/config';
import {
  Figure, Overlay, Scrim, Ring, SelectionFill, Skeleton, RegionLabel, LoadError,
  DrawImage, AnnotateOverlay, CaptureLayer, Toolbar, ToolGroup, ToolButton,
  ToolIcon, ZoomLabel, Hint, HintButton,
} from './ClarificationImage.styled';

export type NormBox = [number, number, number, number];

export interface ClarificationImageProps {
  artifactId: string;
  region?: NormBox;
  regionLabel?: string;
  alt?: string;
  /** Annotate mode (AIB-7965): the user drags a box and we report it + a crop. */
  annotate?: boolean;
  /** Previously drawn box to restore when returning to this question. */
  value?: NormBox;
  /** Called on each finished/clear draw with the region and a PNG crop of it. */
  onRegionDraw?: (region: NormBox | undefined, crop: Blob | null) => void;
}

const artifactUrl = (artifactId: string): string =>
  `${API_BASE_URL}/api/v1/artifacts/${encodeURIComponent(artifactId)}/download`;

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const ZOOM_STEP = 1.5;
const WHEEL_STEP = 1.15;

/** A box counts as soon as it has any area — a bare click (zero area) doesn't. */
const hasArea = (b: NormBox): boolean => b[2] > b[0] && b[3] > b[1];

/** Order two corners into a [x0,y0,x1,y1] box with x0<=x1 and y0<=y1. */
const orderBox = (ax: number, ay: number, bx: number, by: number): NormBox => [
  Math.min(ax, bx), Math.min(ay, by), Math.max(ax, bx), Math.max(ay, by),
];

const clampScale = (s: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/** Keep the scaled content covering the viewport (no gaps at the edges). */
const clampPan = (tx: number, ty: number, s: number, w: number, h: number): [number, number] => [
  Math.min(0, Math.max(w * (1 - s), tx)),
  Math.min(0, Math.max(h * (1 - s), ty)),
];

// Fraction of the box added on every side as surrounding context (with a px
// floor so tiny boxes still get a readable margin).
const CROP_PAD_FRACTION = 0.3;
const CROP_PAD_MIN_PX = 48;

/**
 * Crop the marked area at native resolution, padded with surrounding context so
 * the agent can read neighbouring dimensions/labels, with the user's exact box
 * burned on so it still knows precisely which feature was circled.
 */
const cropRegion = async (img: HTMLImageElement, box: NormBox): Promise<Blob | null> => {
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return null;
  const bx = box[0] * nw;
  const by = box[1] * nh;
  const bw = (box[2] - box[0]) * nw;
  const bh = (box[3] - box[1]) * nh;
  const padX = Math.max(bw * CROP_PAD_FRACTION, CROP_PAD_MIN_PX);
  const padY = Math.max(bh * CROP_PAD_FRACTION, CROP_PAD_MIN_PX);
  const sx = Math.max(0, Math.round(bx - padX));
  const sy = Math.max(0, Math.round(by - padY));
  const sw = Math.max(1, Math.min(nw, Math.round(bx + bw + padX)) - sx);
  const sh = Math.max(1, Math.min(nh, Math.round(by + bh + padY)) - sy);
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  ctx.strokeStyle = '#DC2626';
  ctx.lineWidth = Math.max(2, Math.round(Math.min(sw, sh) * 0.01));
  ctx.strokeRect(Math.round(bx - sx), Math.round(by - sy), Math.round(bw), Math.round(bh));
  return new Promise((resolve) => {
    if (!canvas.toBlob) {
      resolve(null);
      return;
    }
    canvas.toBlob((b) => resolve(b), 'image/png');
  });
};

/**
 * Annotate surface (AIB-7965): the user zooms/pans a drawing and drags a box on
 * it. The box is reported in the same 0-1 fraction space the agent's highlight
 * uses (so it survives zoom/pan) and yields a padded, native-resolution PNG crop
 * of the marked area. Zoom/pan is hand-rolled because ZoomableImage only offers
 * click-to-fullscreen. A Draw/Pan toggle decides whether a drag draws or pans.
 */
const AnnotateSurface: React.FC<{
  blobUrl: string;
  alt: string;
  value?: NormBox;
  onRegionDraw?: (region: NormBox | undefined, crop: Blob | null) => void;
}> = ({ blobUrl, alt, value, onRegionDraw }) => {
  const intl = useIntl();
  const viewportRef = React.useRef<HTMLElement>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const drawing = React.useRef(false);
  const startRef = React.useRef<[number, number] | null>(null);
  // Held in a ref (not just state) so the pointerup handler reads the latest box
  // even when down/move/up fire back-to-back before a re-render.
  const boxRef = React.useRef<NormBox | null>(value ?? null);
  const panRef = React.useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const [draft, setDraft] = React.useState<NormBox | null>(null);
  // Seed from the stored box so returning to this question shows the earlier
  // selection instead of a blank sheet.
  const [committed, setCommitted] = React.useState<NormBox | null>(value ?? null);
  const [mode, setMode] = React.useState<'draw' | 'pan'>('draw');
  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);

  // Client point → 0-1 image fraction, undoing the current zoom/pan transform.
  const toNorm = (clientX: number, clientY: number): [number, number] => {
    const r = viewportRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return [0, 0];
    const ix = (clientX - r.left - tx) / scale;
    const iy = (clientY - r.top - ty) / scale;
    return [clamp01(ix / r.width), clamp01(iy / r.height)];
  };

  const zoomTo = (focalX: number, focalY: number, nextRaw: number) => {
    const r = viewportRef.current?.getBoundingClientRect();
    if (!r) return;
    const next = clampScale(nextRaw);
    const fx = focalX - r.left;
    const fy = focalY - r.top;
    let ntx = next === 1 ? 0 : fx - (fx - tx) * (next / scale);
    let nty = next === 1 ? 0 : fy - (fy - ty) * (next / scale);
    [ntx, nty] = clampPan(ntx, nty, next, r.width, r.height);
    setScale(next);
    setTx(ntx);
    setTy(nty);
  };

  const zoomButton = (dir: 1 | -1) => {
    const r = viewportRef.current?.getBoundingClientRect();
    if (!r) return;
    zoomTo(r.left + r.width / 2, r.top + r.height / 2, dir > 0 ? scale * ZOOM_STEP : scale / ZOOM_STEP);
  };

  const resetView = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomTo(e.clientX, e.clientY, scale * (e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, tx, ty]);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (mode === 'pan') {
      panRef.current = { x: e.clientX, y: e.clientY, tx, ty };
      return;
    }
    drawing.current = true;
    const [x, y] = toNorm(e.clientX, e.clientY);
    startRef.current = [x, y];
    boxRef.current = [x, y, x, y];
    setDraft(boxRef.current);
    setCommitted(null);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (panRef.current) {
      const r = viewportRef.current?.getBoundingClientRect();
      if (!r) return;
      const [ntx, nty] = clampPan(
        panRef.current.tx + (e.clientX - panRef.current.x),
        panRef.current.ty + (e.clientY - panRef.current.y),
        scale, r.width, r.height,
      );
      setTx(ntx);
      setTy(nty);
      return;
    }
    if (!drawing.current || !startRef.current) return;
    const [x, y] = toNorm(e.clientX, e.clientY);
    boxRef.current = orderBox(startRef.current[0], startRef.current[1], x, y);
    setDraft(boxRef.current);
  };

  const onPointerUp = async (e: React.PointerEvent) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (panRef.current) {
      panRef.current = null;
      return;
    }
    if (!drawing.current) return;
    drawing.current = false;
    const box = boxRef.current;
    setDraft(null);
    if (!box || !hasArea(box)) {
      setCommitted(null);
      onRegionDraw?.(undefined, null);
      return;
    }
    setCommitted(box);
    const crop = imgRef.current ? await cropRegion(imgRef.current, box) : null;
    onRegionDraw?.(box, crop);
  };

  const clear = () => {
    boxRef.current = null;
    setDraft(null);
    setCommitted(null);
    onRegionDraw?.(undefined, null);
  };

  const shown = draft ?? committed;

  return (
    <>
      <Toolbar>
        <ToolGroup>
          <ToolButton
            type="button" $active={mode === 'draw'} data-testid="annotate-mode-draw"
            onClick={() => setMode('draw')}
          >
            <ToolIcon>draw</ToolIcon>
            <FormattedMessage id="chat.clarify.drawMode" defaultMessage="Draw" />
          </ToolButton>
          <ToolButton
            type="button" $active={mode === 'pan'} data-testid="annotate-mode-pan"
            onClick={() => setMode('pan')}
          >
            <ToolIcon>pan_tool</ToolIcon>
            <FormattedMessage id="chat.clarify.panMode" defaultMessage="Pan" />
          </ToolButton>
        </ToolGroup>
        <ToolGroup $push>
          <ToolButton
            type="button" data-testid="annotate-zoom-out"
            title={intl.formatMessage({ id: 'chat.clarify.zoomOut', defaultMessage: 'Zoom out' })}
            disabled={scale <= MIN_SCALE} onClick={() => zoomButton(-1)}
          >
            <ToolIcon>zoom_out</ToolIcon>
          </ToolButton>
          <ZoomLabel data-testid="annotate-zoom-level">{Math.round(scale * 100)}%</ZoomLabel>
          <ToolButton
            type="button" data-testid="annotate-zoom-in"
            title={intl.formatMessage({ id: 'chat.clarify.zoomIn', defaultMessage: 'Zoom in' })}
            disabled={scale >= MAX_SCALE} onClick={() => zoomButton(1)}
          >
            <ToolIcon>zoom_in</ToolIcon>
          </ToolButton>
          <ToolButton
            type="button" data-testid="annotate-zoom-reset"
            title={intl.formatMessage({ id: 'chat.clarify.zoomReset', defaultMessage: 'Reset zoom' })}
            disabled={scale === 1} onClick={resetView}
          >
            <ToolIcon>restart_alt</ToolIcon>
          </ToolButton>
        </ToolGroup>
      </Toolbar>
      <Figure ref={viewportRef} data-testid="clarification-annotate">
        <DrawImage ref={imgRef} src={blobUrl} alt={alt} draggable={false} $s={scale} $tx={tx} $ty={ty} />
        {shown && (
          <AnnotateOverlay viewBox="0 0 1 1" preserveAspectRatio="none" $s={scale} $tx={tx} $ty={ty}>
            <SelectionFill
              x={shown[0]}
              y={shown[1]}
              width={Math.max(0, shown[2] - shown[0])}
              height={Math.max(0, shown[3] - shown[1])}
            />
            <Ring
              x={shown[0]}
              y={shown[1]}
              width={Math.max(0, shown[2] - shown[0])}
              height={Math.max(0, shown[3] - shown[1])}
              data-testid="clarification-draw-box"
            />
          </AnnotateOverlay>
        )}
        <CaptureLayer
          $mode={mode}
          data-testid="clarification-draw-surface"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
      </Figure>
      <Hint>
        {committed ? (
          <FormattedMessage
            id="chat.clarify.drawnHint"
            defaultMessage="Area marked. Drag again to redraw."
          />
        ) : (
          <FormattedMessage
            id="chat.clarify.drawHint"
            defaultMessage="Drag to mark the area — zoom in for small details."
          />
        )}
        <HintButton type="button" onClick={clear} disabled={!committed && !draft}>
          <FormattedMessage id="chat.clarify.drawClear" defaultMessage="Clear" />
        </HintButton>
      </Hint>
    </>
  );
};

/**
 * Source drawing shown above a clarification question (AIB-7965). Two modes:
 * confirm mode spotlights the agent-supplied region (a dark scrim dims the rest
 * of the sheet, a crisp outline marks the edge); annotate mode lets the user
 * drag their own box, reporting it in the same 0-1 fraction viewBox and handing
 * back a native-resolution crop of the marked area.
 */
const ClarificationImage: React.FC<ClarificationImageProps> = ({
  artifactId,
  region,
  regionLabel,
  alt,
  annotate = false,
  value,
  onRegionDraw,
}) => {
  const intl = useIntl();
  const resolvedAlt = alt ?? intl.formatMessage({
    id: 'chat.clarify.imageAlt', defaultMessage: 'Highlighted drawing',
  });
  const { url: blobUrl, hasError } = useAuthImage(artifactId ? artifactUrl(artifactId) : undefined);
  // useId can contain ':' which is invalid inside a url(#…) reference.
  const maskId = `clar-spotlight-${React.useId().replace(/:/g, '')}`;

  if (hasError) {
    return (
      <LoadError data-testid="clarification-image-error">
        <FormattedMessage
          id="chat.clarify.imageError"
          defaultMessage="Couldn't load the drawing."
        />
      </LoadError>
    );
  }
  if (!blobUrl) return <Skeleton data-testid="clarification-image-skeleton" />;

  if (annotate) {
    return <AnnotateSurface blobUrl={blobUrl} alt={resolvedAlt} value={value} onRegionDraw={onRegionDraw} />;
  }

  const w = region ? Math.max(0, region[2] - region[0]) : 0;
  const h = region ? Math.max(0, region[3] - region[1]) : 0;

  return (
    <>
      <Figure data-testid="clarification-image">
        <ZoomableImage src={blobUrl} alt={resolvedAlt} />
        {region && (
          <Overlay viewBox="0 0 1 1" preserveAspectRatio="none" data-testid="clarification-image-ring">
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
                <rect x={0} y={0} width={1} height={1} fill="white" />
                <rect x={region[0]} y={region[1]} width={w} height={h} fill="black" />
              </mask>
            </defs>
            <Scrim x={0} y={0} width={1} height={1} mask={`url(#${maskId})`} />
            <Ring
              x={region[0]}
              y={region[1]}
              width={w}
              height={h}
              data-testid="clarification-image-focus"
            />
          </Overlay>
        )}
      </Figure>
      {regionLabel && <RegionLabel>{regionLabel}</RegionLabel>}
    </>
  );
};

export default ClarificationImage;
